import { MavLinkProtocolV2, common, minimal, ardupilotmega } from '@m1/mavlink';
import useMountEffect from '@restart/hooks/useMountEffect';
import React, { createContext, useMemo, useRef } from 'react';
import { useDispatch, useStore } from 'react-redux';
import { useLocation } from 'react-router-dom';

import actions from '@/actions';
import { MAVLINK_MESSAGE_IDS as MESSAGE } from '@/define/messageId';
import messageSet from '@/define/messageSet';
import dayjs from '@/libs/dayjs';
import EventEmitter from '@/libs/EventEmitter';
import * as sheco from '@/models/mavlink/px4-sheco/messages';
import telemetryStore from '@/stores/telemetryStore';

const MessageContext = createContext(null);
export { MessageContext };

const IMMEDIATE_MSG_IDS = new Set([
  MESSAGE.PARAM_VALUE,
  MESSAGE.MISSION_REQUEST,
  MESSAGE.MISSION_COUNT,
  MESSAGE.MISSION_ACK,
  MESSAGE.MISSION_REQUEST_INT,
  MESSAGE.MISSION_ITEM_INT,
  MESSAGE.COMMAND_ACK,
  MESSAGE.STATUSTEXT,
]);

const Provider = ({ children }) => {
  const location = useLocation();
  const dispatch = useDispatch();
  const store = useStore();
  const mqttEdgeWorker = useRef();
  const mqttViewerWorker = useRef();
  const robotMeta = useRef({});
  const registry = useRef({
    ...minimal.REGISTRY,
    ...common.REGISTRY,
    ...ardupilotmega.REGISTRY,
    ...sheco.REGISTRY,
  });

  const pendingTelemetry = useRef(new Map());
  const rafId = useRef(null);

  const key = useMemo(() => {
    return location.pathname.split('/')[1];
  }, [location]);

  useMountEffect(() => {
    // MQTT Edge Worker 정의
    mqttEdgeWorker.current = new Worker('/workers/mqtt-edge-worker.js');
    // MQTT 연결
    mqttEdgeWorker.current.postMessage({
      command: 'connect',
      data: {
        brokerUrl: process.env.REACT_APP_MQTT_EDGE_URL,
        username: process.env.REACT_APP_MQTT_EDGE_USERNAME,
        password: process.env.REACT_APP_MQTT_EDGE_PASSWORD,
      },
    });

    // MQTT Viewer Worker 정의
    mqttViewerWorker.current = new Worker('/workers/mqtt-viewer-worker.js');
    // MQTT 연결
    mqttViewerWorker.current.postMessage({
      command: 'connect',
      data: {
        brokerUrl: process.env.REACT_APP_MQTT_VIEWER_URL,
        username: process.env.REACT_APP_MQTT_VIEWER_USERNAME,
        password: process.env.REACT_APP_MQTT_VIEWER_PASSWORD,
      },
    });
    // 메시지 수신 처리
    mqttViewerWorker.current.onmessage = ({ data }) => messageHandler(data);

    // useAuthGuard 내 강제 로그아웃 이벤트 발생 시 MQTT Worker 종료
    const subscribeToken = EventEmitter.subscribe('force-logout', () => {
      terminateWorkers();
    });

    return () => {
      EventEmitter.unsubscribe(subscribeToken);
      terminateWorkers();

      if (rafId.current) {
        cancelAnimationFrame(rafId.current);
      }
    };
  });

  const terminateWorkers = () => {
    const workers = [mqttEdgeWorker, mqttViewerWorker];
    workers.forEach((worker) => {
      if (worker.current) {
        worker.current.onmessage = null;
        worker.current.terminate();
        worker.current = null;
      }
    });
  };

  const messageHandler = ({ robotId, type, message }) => {
    // 텔레메트리 수신 전 로봇 메타 데이터 초기화되지 않은 상태 시
    if (!robotMeta.current[robotId]) return;

    const { isInitialized, isActive, isShow } = store.getState().robots.find(({ id }) => id === robotId);

    // 초기화되지 않은 로봇인 경우
    if (!isInitialized) {
      telemetryStore.init(robotId);

      if (key === 'control-center') {
        dispatch(actions.robotOptions.init(robotId));
        dispatch(actions.robotParams.init(robotId));
      }
      dispatch(actions.address.init(robotId));
      dispatch(actions.robotStatus.init(robotId));
      dispatch(actions.robots.init(robotId));
    }
    // 노출 로봇이 아닌 경우
    if (!isShow && key === 'mission-hub') return;

    // 타입별 메시지 처리
    switch (type) {
      case 'telemetry':
        if (!isActive) {
          dispatch(actions.robots.activate(robotId));
        }
        handleMessageTelemetry(robotId, message);
        break;

      case 'iothub':
        if (!isActive) {
          dispatch(actions.robots.activate(robotId));
        }
        handleMessageIoTHub(robotId, message);
        break;

      default:
        break;
    }
  };

  // Telemetry 메시지 처리
  const handleMessageTelemetry = (robotId, message) => {
    const buffer = Buffer.from(message);
    const { header, payload } = JSON.parse(buffer.toString());

    // Component ID가 1이 아닌 경우 처리 제외
    if (header.compid !== 1) return;

    // 즉시 처리 필요한 메시지 경우
    if (IMMEDIATE_MSG_IDS.has(header.msgid)) {
      EventEmitter.publish(`${robotId}/telemetry/mavlink`, { header, payload });
      return;
    }

    const topic = `${robotId}/telemetry/mavlink`;
    pendingTelemetry.current.set(`${robotId}:${header.msgid}`, { topic, header, payload });

    if (!rafId.current) {
      rafId.current = requestAnimationFrame(flushPendingTelemetry);
    }
  };

  const flushPendingTelemetry = () => {
    rafId.current = null;

    const snapshot = pendingTelemetry.current;
    if (snapshot.size === 0) return;

    pendingTelemetry.current = new Map();

    // for...of → 콜백 오버헤드 없이 순회
    for (const { topic, header, payload } of snapshot.values()) {
      EventEmitter.publish(topic, { header, payload });
    }
  };

  // IoTHub 메시지 처리
  const handleMessageIoTHub = (robotId, message) => {
    const buffer = Buffer.from(message);
    const { action, data } = JSON.parse(buffer.toString());

    EventEmitter.publish(`${robotId}/event/${action}`, data);
  };

  const subscribeTelemetry = (robots) => {
    robots.forEach((robot) => {
      robotMeta.current[robot.id] = {
        commandSeq: 0,
        agentType: robot.model.agentType,
      };

      // 구독 토픽 정의
      const topics = [`robots/${robot.id}/telemetry`, `robots/${robot.id}/iothub`];

      // 메시지 구독 요청
      mqttViewerWorker.current.postMessage({
        command: 'subscribe',
        data: { topics },
      });
    });
  };

  const publishCommand = (robot, name, data) => {
    // 텔레메트리 수신 전 로봇 메타 데이터 초기화되지 않은 상태 시
    if (!robotMeta.current[robot.id]) return;

    publishMavLinkCommand(robot, name, data);
  };

  const publishMavLinkCommand = (robot, name, paramsSet) => {
    if (!mqttEdgeWorker.current) return;

    const { sysId, compId, messages } = messageSet.mavlink[name];
    const protocol = new MavLinkProtocolV2(sysId, compId);

    for (let messageIndex = 0; messageIndex < messages.length; messageIndex++) {
      const message = messages[messageIndex];
      const { isMultiple, msgId, fields } = message;
      const paramsGroup = [];

      // 반복 메시지 경우
      if (isMultiple) {
        paramsSet.shift().forEach((params) => paramsGroup.push(params));
      }
      // 단일 메시지 경우
      else {
        paramsGroup.push(paramsSet.shift());
      }

      for (let paramsIndex = 0; paramsIndex < paramsGroup.length; paramsIndex++) {
        const params = paramsGroup[paramsIndex];
        const mavMessage = new registry.current[msgId]();
        const mavMessageFields = registry.current[msgId].FIELDS;

        fields.forEach((field) => {
          const { name } = mavMessageFields.find(({ source }) => source === field.name);

          // 변수인 경우
          if (field.isVariable) {
            mavMessage[name] = params.shift();
          } else {
            mavMessage[name] = field.value;
          }
        });

        const seq = robotMeta.current[robot.id].commandSeq;
        robotMeta.current[robot.id].commandSeq = (seq + 1) % 256;

        let message = protocol.serialize(mavMessage, seq);

        //#region M1Edge 통신 단절 중 발송된 메시지의 지연된 작용 방지를 위한 처리
        const timestamp = dayjs().valueOf();
        const timestampBuffer = Buffer.alloc(8);
        timestampBuffer.writeBigUInt64BE(BigInt(timestamp * 1000));
        message = Buffer.concat([timestampBuffer, message]);
        //#endregion

        mqttEdgeWorker.current.postMessage({
          command: 'publish',
          data: {
            topic: `robots/${robot.id}/command/air`,
            message,
          },
        });
      }
    }
  };

  const publishEvent = (robot, event, data) => {
    mqttEdgeWorker.current.postMessage({
      command: 'publish',
      data: {
        topic: `robots/${robot.id}/event`,
        message: JSON.stringify({ event, data }),
      },
    });
  };

  return (
    <MessageContext.Provider value={{ subscribeTelemetry, publishCommand, publishEvent }}>
      {children}
    </MessageContext.Provider>
  );
};

export default Provider;
